import {
    FormControl,
    Grid,
    Paper,
    Typography,
    IconButton,
    Tooltip,
    Button,
    ToggleButtonGroup,
    ToggleButton,
    Box,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../reducers';
import { useAppDispatch } from '../../../../store/store';
import { parentUrl } from '../../../../utils/constants';
import { dateFormatter } from '../../../../utils/timeFormat';
import WidgetLoading from '../../Shared/WidgetLoading';
import { generateRandomId, isDefaultDate } from '../../../../utils/common';
import { ConsentDocumentTypeEnum } from '../../../../Enum/ConsentDocumentTypeEnum';
import Authorization from '../../../../utils/Authorization';
import b64toBlob from 'b64-to-blob';
import TeamDropdown from '../../../Shared/TeamDropdown';
import { fetchMissingOrExpiredConsents, exportMissingOrExpiredConsents } from '../../../Widgets/WidgetService';
import { fetchTeams } from '../../../../store/careTeams.slice';
import { DataGrid, gridClasses, GridColDef } from '@mui/x-data-grid';

const consentTypeList = [
    { Id: ConsentDocumentTypeEnum.PartOne, Value: ConsentDocumentTypeEnum.PartOneDescription },
    { Id: ConsentDocumentTypeEnum.PartTwo, Value: ConsentDocumentTypeEnum.PartTwoDescription },
];

const MissingOrExpiredConsents = () => {
    const auth = new Authorization();
    const dispatch = useAppDispatch();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const [teamId, setTeamId] = useState(auth.getDefaultTeamId('All'));
    const [documentType, setDocumentType] = useState(1);
    const [membersWithNoConsent, setMembersWithNoConsent] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [show, setShow] = useState(false);
    const [showInActiveTeams, setShowInActiveTeams] = useState(false);
    const ref = useRef(null);
    const [downLoading, setDownLoading] = useState<boolean>(false);

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = (event) => {
        setShow(!show);
    };

    const columns: GridColDef[] = [
        {
            field: 'Index',
            headerName: '#',
            width: 60,
            renderCell: (params) => {
                return <Typography>{params.api.getSortedRowIds().indexOf(params.id) + 1}</Typography>;
            },
            minWidth: 50,
            flex: 1,
        },
        {
            field: 'LastName',
            headerName: 'Name',
            minWidth: 175,
            flex: 1,
            renderCell: (params) => {
                return <>{params.row.LastName} {params.row.FirstName}</>;
            },
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            minWidth: 105,
            flex: 1,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.DOB) ? '-' : dateFormatter(new Date(params.row.DOB))}</>;
            },
        },
        {
            field: 'ExpirationDate',
            headerName: 'Expiration Date',
            minWidth: 120,
            flex: 1.2,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.ExpirationDate) ? '-' : dateFormatter(new Date(params.row.ExpirationDate))}</>;
            },
        },
        {
            field: 'LastCHPVisit',
            headerName: 'Last CHP Visit',
            minWidth: 115,
            flex: 1.2,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.LastCHPVisit) ? '-' : dateFormatter(new Date(params.row.LastCHPVisit))}</>;
            },
        },
        {
            field: 'NextCHPVisit',
            headerName: 'Next CHP Visit',
            minWidth: 115,
            flex: 1.2,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.NextCHPVisit) ? '-' : dateFormatter(new Date(params.row.NextCHPVisit))}</>;
            },
        },

    ];

    useEffect(() => {
        const changeFilter = async () => {
            try {
                setIsLoading(true);
                const response = await fetchMissingOrExpiredConsents(teamId === '-1' || teamId === undefined ? '' : teamId, documentType, showInActiveTeams);
                setMembersWithNoConsent(response);
                setIsLoading(false);
            } catch (error) {
                setMembersWithNoConsent([]);
                setIsLoading(false);
            }
        };
        changeFilter();
    }, [teamId, documentType, showInActiveTeams]);

    const exportMemberWithNoConsentForms = async () => {
        setDownLoading(true);
        const response = await exportMissingOrExpiredConsents(teamId === '-1' || teamId === undefined ? '' : teamId, showInActiveTeams);
        let blob = b64toBlob(response.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `MissingOrExpiredConsents - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Grid container spacing={2}>
                    <Grid size={12} textAlign="center">
                        <Typography display={'inline-block'} variant="h6" marginRight={1} color={'primary'}>
                            Missing or Expired Consents
                        </Typography>
                        <Typography display={'inline-block'} variant="body1" color={'primary'} ref={ref}>
                            &#40;{membersWithNoConsent.length}&#41;
                            <Tooltip
                                open={show}
                                title={
                                    <div>
                                        <Typography variant="caption">
                                            This widget flags members whose consent forms are either missing or expired.
                                            Use this widget to review the list to ensure documentation is current and compliant with care coordination requirements.
                                        </Typography>
                                    </div>
                                }
                                PopperProps={{ style: { zIndex: 1000 } }}
                            >
                                <IconButton onClick={handleClick}>
                                    <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                </IconButton>
                            </Tooltip>
                        </Typography>
                    </Grid>
                    <Grid size={3}>
                        <TeamDropdown
                            teams={teams}
                            selectedTeam={teamId}
                            onChange={(id) => setTeamId(id)}
                            showInactive={showInActiveTeams}
                            onToggleInactive={setShowInActiveTeams}
                        />
                    </Grid>
                    <Grid size={3}>
                        <FormControl className="form-control-200" size="small">
                            <ToggleButtonGroup exclusive fullWidth value={documentType} onChange={(event, newValue) => {
                                if (newValue !== null) {
                                    setDocumentType(newValue);
                                }
                            }}>
                                {consentTypeList.map((item: any, index) => {
                                    return (
                                        <ToggleButton
                                            size="small"
                                            key={index}
                                            color="primary"
                                            value={item.Id}
                                        >
                                            {item.Value}
                                        </ToggleButton>
                                    );
                                })}
                            </ToggleButtonGroup>
                        </FormControl>
                    </Grid>
                    <Grid size={4}></Grid>
                    <Grid size={2} display={'flex'} maxHeight="55px">
                        <Button
                            style={{ minWidth: '100px', marginLeft: 'auto' }}
                            onClick={() => exportMemberWithNoConsentForms()}
                            variant="contained"
                            color="success"
                            disabled={downLoading || membersWithNoConsent?.length === 0}
                        >
                            {downLoading ? 'Exporting...' : 'Export'}
                        </Button>
                    </Grid>
                </Grid>
                <Box display="flex" position="relative" height='calc(100vh - 285px)' paddingTop={2}>
                    {
                        isLoading ? <WidgetLoading />
                            : membersWithNoConsent.length > 0 ?
                                <Grid size={12} style={{ maxHeight: '500px', height: '75vh', width: '100%' }}>
                                    <DataGrid
                                        columns={columns}
                                        rows={membersWithNoConsent}
                                        getRowId={(row) => `${row.Id}-${generateRandomId()}`}
                                        paginationMode="server"
                                        hideFooterPagination={true}
                                        hideFooter={true}
                                        onCellClick={(params: any) => {
                                            window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/member-profile`, '_blank');
                                        }}
                                        sx={{
                                            [`& .${gridClasses.cell}`]: {
                                                py: 2,
                                            },
                                        }}
                                    />
                                </Grid>
                                : <Box display="flex" width="100%" justifyContent="center" alignItems="center">
                                    <Typography variant="h6">No Matching Records</Typography>
                                </Box>
                    }
                </Box>
            </Paper>
        </Grid>
    );
};

export default MissingOrExpiredConsents;
